/**
 * The periods the Fixed Income tab's Weekly and Daily perspectives offer, and
 * what each one is compared against.
 *
 * Clearwater delivers positions on business days only, so every "previous"
 * answer here is "the previous day we HAVE". A fixed one-day step would compare a
 * Monday against an absent Sunday and report the entire book as new; a fixed
 * seven-day step would do the same across a holiday week.
 *
 * Pure functions over ISO 'YYYY-MM-DD' strings — this is where the off-by-one-day
 * mistakes live, so it is tested without rendering anything.
 */
import dayjs from 'dayjs';

const ISO = 'YYYY-MM-DD';

/**
 * The three perspectives, coarsest first.
 *
 * 'monthly' is the existing month-end recon (Clearwater against the custodian
 * files, via /recon-dates); 'weekly' and 'daily' read the daily Clearwater
 * positions. That split is the reason this module returns nothing for 'monthly'.
 */
export const GRANULARITIES = ['monthly', 'weekly', 'daily'];

export const GRANULARITY_LABELS = { monthly: 'Monthly', weekly: 'Weekly', daily: 'Daily' };

const toDay = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = dayjs(String(value).slice(0, 10));
  return parsed.isValid() ? parsed : null;
};

/**
 * The ISO day a value names, or null. Exported so every consumer keys dates the
 * same way — a Set built from raw payload strings will not match a normalized
 * lookup, and the mismatch is silent.
 */
export const toIsoDay = (value) => {
  const day = toDay(value);
  return day ? day.format(ISO) : null;
};

/** Valid ISO days, oldest first, de-duplicated. */
const cleanDates = (dates) => {
  const seen = new Set();
  (Array.isArray(dates) ? dates : []).forEach((raw) => {
    const day = toDay(raw);
    if (day) seen.add(day.format(ISO));
  });
  return [...seen].sort();
};

/**
 * Available days bucketed into Sunday-based weeks, newest week first.
 *
 * `start`/`end` are the calendar week; `days` are only the days Clearwater
 * actually delivered, and `close` is the last of those — the current week closes
 * on the latest day it holds, not on a Friday that has not happened yet.
 */
/**
 * Is this week still running?
 *
 * The newest week closes on whatever day Clearwater has delivered so far, which
 * on a Wednesday is two days of movement. Compared against a full prior week
 * that reads as a collapse in activity, so a week short of its days is labelled
 * rather than presented as a finished period.
 *
 * Two ways a week is done: its calendar end has passed, or it has already
 * delivered its Friday — waiting for Saturday would spend all of Friday telling
 * the reader that a complete Monday-to-Friday week holds fewer days than it
 * does.
 *
 * A week whose Friday is a holiday still reads as running until Saturday. That
 * is the conservative direction (it under-claims completeness rather than
 * over-claiming it) and undoing it needs a business calendar this app does not
 * have.
 */
export function isWeekInProgress(week, today = dayjs().format(ISO)) {
  if (!week?.end || !week?.start) return false;
  if (week.end <= String(today)) return false;
  const friday = dayjs(week.start).add(5, 'day').format(ISO);
  return !week.close || week.close < friday;
}

export function groupDatesIntoWeeks(dates) {
  const buckets = new Map();
  cleanDates(dates).forEach((iso) => {
    const day = dayjs(iso);
    const start = day.startOf('week').format(ISO);
    if (!buckets.has(start)) {
      buckets.set(start, {
        start,
        end: day.endOf('week').format(ISO),
        days: [],
      });
    }
    buckets.get(start).days.push(iso);
  });

  return [...buckets.values()]
    .map((week) => ({ ...week, close: week.days[week.days.length - 1] }))
    .sort((a, b) => (a.start < b.start ? 1 : -1));
}

/**
 * How a week reads in the picker: the span of the days it holds, not the
 * calendar week, so a three-day holiday week says so.
 */
export function weekLabel(week) {
  const first = toDay(week?.days?.[0]);
  const last = toDay(week?.close);
  if (!first || !last) return '';
  if (first.isSame(last, 'day')) return first.format('MMM D, YYYY');
  if (first.year() !== last.year()) {
    return `${first.format('MMM D, YYYY')} – ${last.format('MMM D, YYYY')}`;
  }
  if (first.month() !== last.month()) {
    return `${first.format('MMM D')} – ${last.format('MMM D, YYYY')}`;
  }
  return `${first.format('MMM D')} – ${last.format('D, YYYY')}`;
}

/** A day reads with its weekday, which is what makes a missing day obvious. */
export const dayLabel = (iso) => {
  const day = toDay(iso);
  return day ? day.format('ddd, MMM D, YYYY') : '';
};

/**
 * The periods on offer for *granularity*, newest first, each carrying the date it
 * is compared against.
 *
 * `compareTo` is computed here and sent to the server explicitly because the two
 * perspectives disagree about it: a week close is compared with the PRIOR WEEK's
 * close, which is not the previous available day (Friday's would be Thursday, the
 * same week).
 */
export function buildPeriodOptions(dates, granularity, { today } = {}) {
  const available = cleanDates(dates);
  if (available.length === 0) return [];

  if (granularity === 'daily') {
    return available
      .map((iso, idx) => ({
        value: iso,
        label: dayLabel(iso),
        compareTo: idx > 0 ? available[idx - 1] : null,
      }))
      .reverse();
  }

  if (granularity === 'weekly') {
    const weeks = groupDatesIntoWeeks(available);
    // weeks are newest first, so the prior week is the NEXT entry.
    return weeks.map((week, idx) => {
      const inProgress = today === undefined
        ? isWeekInProgress(week)
        : isWeekInProgress(week, today);
      return {
        value: week.close,
        label: inProgress ? `${weekLabel(week)} (week to date)` : weekLabel(week),
        compareTo: idx + 1 < weeks.length ? weeks[idx + 1].close : null,
        inProgress,
        week,
      };
    });
  }

  // Monthly is the month-end recon; it draws its dates from /recon-dates.
  return [];
}

const COMPARISON_LABELS = {
  daily: 'vs previous business day',
  weekly: 'vs prior week close',
};

/**
 * Resolve the selected period into what the request and the header need.
 *
 * An unavailable selection falls back to the newest period rather than to
 * nothing: switching from Daily (a Thursday) to Weekly must land on the newest
 * week, not on an empty screen.
 */
export function resolvePeriod(dates, granularity, selected, options$ = {}) {
  const options = buildPeriodOptions(dates, granularity, options$);
  if (options.length === 0) return null;
  const option = options.find((o) => o.value === selected) || options[0];
  return {
    asofdt: option.value,
    compareTo: option.compareTo,
    label: option.label,
    inProgress: Boolean(option.inProgress),
    comparisonLabel: [
      option.compareTo
        ? `${COMPARISON_LABELS[granularity] || 'vs'}, ${option.compareTo}`
        : 'no earlier snapshot to compare against',
      // A part-week against a whole one is not a like-for-like comparison, and
      // the shortfall reads as a collapse in activity unless it is said.
      option.inProgress ? '— this week is still running, so it holds fewer days' : '',
    ].filter(Boolean).join(' '),
    week: option.week,
  };
}
